import type { ColDef } from "ag-grid-community";

/**
 * Returns the AG Grid column id for `col`: `colId` if set, else `field`,
 * else `""`. Mirrors AG Grid's own id resolution so preference snapshots
 * and column-picker options key off the same string AG Grid uses
 * internally. A column with neither `colId` nor `field` has no id in
 * AG Grid either, so `""` is a faithful projection rather than a
 * defensive default.
 */
export function colId<T>(col: ColDef<T>): string {
  return col.colId ?? col.field ?? "";
}

export interface BuildColumnsForPreferencesParams<TData> {
  /**
   * Always-present column definitions, e.g. record-level static fields.
   */
  staticColumns: ColDef<TData>[];

  /**
   * Column definitions sourced from the server (e.g. per-org metadata
   * keys, custom fields).
   */
  dynamicColumns: ColDef<TData>[];

  /**
   * Ordered list of column ids from the user's stored prefs snapshot.
   */
  preferenceOrder: readonly string[];
}

/**
 * Compose a consumer's column definitions in preference-driven order.
 *
 * `DataTable.Table` ships a `DEFAULT_COL_DEF` with `hide: true`, so every
 * column starts hidden until something forces `hide: false`. Any column
 * whose id appears in `preferenceOrder` is emitted with `hide: false`;
 * to make a column visible by default, pass its id (via `colId(col)`) in
 * both `staticColumns` and `preferenceOrder`.
 */
export function buildColumnsForPreferences<TData>({
  staticColumns,
  dynamicColumns,
  preferenceOrder,
}: BuildColumnsForPreferencesParams<TData>): ColDef<TData>[] {
  // `Map` iteration is insertion order (ES2015 spec). `byId.set(id, …)`
  // on an existing key keeps the original position and replaces the
  // value — that's how preference-ordered stubs survive being overlaid with
  // a static or dynamic ColDef without moving.
  const byId = new Map<string, ColDef<TData>>();
  const fromPreferences = new Set(preferenceOrder);

  for (const id of preferenceOrder) {
    if (!byId.has(id)) {
      byId.set(id, { colId: id, hide: false });
    }
  }

  for (const col of staticColumns) {
    const id = colId(col);
    byId.set(id, fromPreferences.has(id) ? { ...col, hide: false } : col);
  }

  for (const col of dynamicColumns) {
    const id = colId(col);
    if (fromPreferences.has(id)) {
      byId.set(id, { ...col, hide: false });
    } else if (!byId.has(id)) {
      byId.set(id, col);
    }
  }

  return Array.from(byId.values());
}
